/**
 * lookbook-drawer.js — shared lookbook quick-view drawer.
 *
 * <lookbook-drawer> is rendered once. Lookbook tiles are triggers ([data-lookbook-open="{id}"]);
 * each ships an inert <template data-lookbook-content="{id}"> (heading + p-c cards). On click we clone
 * the matching template into the drawer (images don't load until then), set the side from the tile's
 * position, and open. Pure-CSS slide + staggered card entrance. Progressive enhancement: with no
 * template (no products) or no JS, the tile's <a href> just navigates.
 */
(function () {
  if (customElements.get('lookbook-drawer')) return;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class LookbookDrawer extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-lb-content]');
      this.lastFocused = null;

      this.onDocClick = this.onDocClick.bind(this);
      this.onDocKeydown = this.onDocKeydown.bind(this);
      this.onSelfClick = this.onSelfClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      document.addEventListener('click', this.onDocClick); // triggers (delegated → survive grid swaps)
      document.addEventListener('keydown', this.onDocKeydown); // keyboard activation for role="button" triggers
      this.addEventListener('click', this.onSelfClick); // overlay + close button
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onDocKeydown);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onDocClick(e) {
      var trigger = e.target.closest('[data-lookbook-open]');
      if (!trigger || this.contains(trigger)) return;

      var id = trigger.getAttribute('data-lookbook-open');
      var selector = 'template[data-lookbook-content="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]';
      var tpl = document.querySelector(selector);
      if (!tpl) return; // no products → let the tile link navigate

      e.preventDefault();
      this.open(tpl, trigger.getAttribute('data-lookbook-side') || 'right');
    }

    onDocKeydown(e) {
      // Lookbook triggers are <a> without href (drawer-only), exposed as role="button".
      // Native buttons fire click on Enter/Space; these don't, so bridge it here.
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var trigger = e.target.closest('[data-lookbook-open]');
      if (!trigger || this.contains(trigger)) return;
      e.preventDefault(); // Space would otherwise scroll the page
      trigger.click(); // reuse the delegated click → open path
    }

    onSelfClick(e) {
      if (e.target.closest('[data-lb-close]')) this.close();
    }

    open(tpl, side) {
      this.lastFocused = document.activeElement;

      // Clone fresh each time — browser-caches images after first open, so no repeat network.
      this.content.replaceChildren(tpl.content.cloneNode(true));

      // Snap the panel to the correct side's off-screen position WITHOUT animating, so it always
      // slides in FROM the tile's side — even when the side changes between opens. (Without this,
      // the panel animates between the two off-screen positions and appears from the previous side.)
      this.classList.add('lb-no-anim');
      this.classList.toggle('lookbook-drawer--left', side === 'left');
      void this.offsetWidth; // commit the off-position instantly (no transition)
      this.classList.remove('lb-no-anim');
      void this.offsetWidth; // commit transition restoration before sliding in

      this.classList.add('is-open');
      this.removeAttribute('aria-hidden');
      document.documentElement.classList.add('ss-scroll-lock');

      // Focus the dialog container (tabindex="-1") rather than the close button — moves focus
      // into the modal for a11y without showing a focus ring on the X every open. Keyboard
      // users see the ring only once they Tab to a real control.
      this.focus();
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ss-scroll-lock');
      document.removeEventListener('keydown', this.onKeydown);

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();

      // Clear the cloned content once the close transition finishes.
      var self = this;
      window.setTimeout(function () {
        if (!self.classList.contains('is-open')) self.content.replaceChildren();
      }, 450);
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    }

    trapFocus(e) {
      var nodes = this.querySelectorAll(FOCUSABLE);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      // We focus the container itself on open (tabindex="-1"); it's not in `nodes`, so an
      // immediate Shift+Tab could escape. Snap focus to an edge instead.
      if (document.activeElement === this) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('lookbook-drawer', LookbookDrawer);
})();
